import { FC, useMemo } from "react";
import { OneTimeSlot } from "../../api/scheduleApi.ts";
import ActionButton from "../ActionButton.tsx";
import Container from "../Container.tsx";
import ContainerHeader from "../ContainerHeader.tsx";
import { Trans } from "@lingui/react/macro";
import { format } from "date-fns";
import { useLanguageStore } from "../../stores/languageStore.ts";
import { LocationWithName } from "../../api/locationApi.ts";

interface OneTimeSlotsListProps {
  oneTimeSlots: OneTimeSlot[];
  onDeleteOneTimeSlot: (id: string) => void;
  deletingId: string | null;
  locations: LocationWithName[];
}

const OneTimeSlotsList: FC<OneTimeSlotsListProps> = ({
  oneTimeSlots,
  onDeleteOneTimeSlot,
  deletingId,
  locations,
}) => {
  const handleDeleteClick = (oneTimeSlot: OneTimeSlot) => {
    onDeleteOneTimeSlot(oneTimeSlot.id!);
  };
  const { dateFnsLocale } = useLanguageStore();
  const sortedOneTimeSlots = useMemo(() => {
    if (oneTimeSlots.length === 0) return [];
    return oneTimeSlots.sort((a, b) => {
      const fromComparison =
        new Date(a.from).getTime() - new Date(b.from).getTime();
      if (fromComparison !== 0) return fromComparison;
      return a.to.getTime() - b.to.getTime();
    });
  }, [oneTimeSlots]);
  return (
    <Container>
      <ContainerHeader>
        <Trans>Your One Time Slots</Trans>
      </ContainerHeader>
      {oneTimeSlots.length === 0 ? (
        <p className="text-gray-500">
          <Trans>No One Time Slots added yet.</Trans>
        </p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {sortedOneTimeSlots.map((oneTimeSlot) => {
            const formattedDate = format(oneTimeSlot.from, "P", {
              locale: dateFnsLocale,
            });

            const isDeleting = deletingId === oneTimeSlot.id;
            const anyDeleting = deletingId !== null;

            const location = locations.find(
              ({ id }) => id === oneTimeSlot.locationId,
            );

            return (
              <li
                key={oneTimeSlot.id}
                className="flex items-center justify-between py-4"
              >
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    {formattedDate}
                  </p>
                  <p className="text-sm text-gray-500">
                    {format(oneTimeSlot.from, "p", {
                      locale: dateFnsLocale,
                    })}{" "}
                    -{" "}
                    {format(oneTimeSlot.to, "p", {
                      locale: dateFnsLocale,
                    })}{" "}
                    • {location?.name}
                  </p>
                </div>
                <ActionButton
                  onClick={() => handleDeleteClick(oneTimeSlot)}
                  disabled={anyDeleting}
                  isLoading={isDeleting}
                  className="ml-4 rounded-full px-3 py-1.5 text-xs"
                >
                  <Trans>Delete</Trans>
                </ActionButton>
              </li>
            );
          })}
        </ul>
      )}
    </Container>
  );
};

export default OneTimeSlotsList;
